// @flow

import * as React from 'react';
import path from 'path';
import url from 'url';

import { connect } from 'react-redux';
import { compose, bindActionCreators } from 'redux';

import { selectFromConfig } from '@gandi/redux-config';
import { localeSelector, withTranslator } from '@gandi/translate';

import { withRouter } from 'src/helpers/withRouter';
import type { Dispatch, FullState, ComponentStatus } from 'src/types';

import {
  fetchStatus,
  selectServiceStatus,
  selectStatusUrl,
} from '../../../../modules/status';
import { selectShowVPSFormCategory } from '../../../../modules/flags';
import ContactSupportForm, {
  type OwnProps,
} from './components/ContactSupportForm';

const selectDisabledTips = selectFromConfig('app.disabledTips', []);
const selectLayoutUrls = selectFromConfig<{| [string]: string |}>(
  'app.layout.urls',
  {}
);

const mapStateToProps = (state: FullState, props: OwnProps) => {
  const locale = localeSelector(state);
  const layoutUrls = selectLayoutUrls(state);
  const publicSite = layoutUrls.public_site || 'https://www.gandi.net';
  const contractsUrl = url.resolve(
    publicSite,
    path.join(locale, 'contracts/terms-of-service')
  );

  return {
    contractsUrl,
    disabledTips: selectDisabledTips(state),
    showVPSFormCategory: selectShowVPSFormCategory(state),
    status: selectServiceStatus(state, props.routeParams.subject),
    statusUrl: selectStatusUrl(state),
  };
};

const mapActionsToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      fetchStatus,
    },
    dispatch
  );

// TODO: Move these to the component instead of mergeProps.
const mergeProps = (
  stateProps: {
    contractsUrl: string,
    disabledTips: { +[string]: mixed },
    showVPSFormCategory: boolean,
    status: void | ComponentStatus,
    statusUrl: any,
    ...
  },
  dispatchProps: Dispatch,
  ownProps: any
) => {
  const accessAndRectificationTips = [
    {
      type: 'info',
      title: ownProps.translator.__('Access your data'),
      message: ownProps.translator.__(
        '<p>' +
          'You may access and rectify your personal data that Gandi has ' +
          'on record from your account’s management page. ' +
          'You can find detailed instructions in doing so in our ' +
          '<a href="https://docs.gandi.net/en/account_management/' +
          'changing_account_information/index.html">documentation</a>.' +
          '</p>\n\n<p>' +
          'You may consult our “Personal Data Protection Policy” which is ' +
          'accessible from our <a href="%(contracts_url)s">Contrats</a> ' +
          'page, to learn how Gandi handles your personal data.' +
          '</p>\n\n<p>' +
          'Please note, however, that in conformity to domain name rules ' +
          'and for legal reasons, some data can not be edited ' +
          '(notably the first and last name).',
        {
          contracts_url: stateProps.contractsUrl,
          withHTML: true,
        }
      ),
    },
  ];

  return {
    ...stateProps,
    ...dispatchProps,
    ...ownProps,
    subjects: [
      {
        text: ownProps.translator.__('Contact Corporate Team'),
        value: 'corporate-contact',
        hidden: true,
      },
      {
        text: ownProps.translator.__('Reseller Request'),
        value: 'reseller-contact',
        hidden: true,
      },
      {
        text: ownProps.translator.__('My rights'),
        value: 'my-rights',
        hidden: true,
      },
      {
        [ownProps.translator.__('Products & Services')]: [
          { text: ownProps.translator.__('Domain'), value: 'domain' },
          { text: ownProps.translator.__('Mail'), value: 'gandimail' },
          { text: ownProps.translator.__('Web Hosting'), value: 'shs' },
          {
            text: ownProps.translator.__('GandiCloud VPS'),
            value: 'vps',
            hidden: !stateProps.showVPSFormCategory,
          },
          { text: ownProps.translator.__('Cloud'), value: 'hosting' },
          {
            text: ownProps.translator.__('Certificate SSL'),
            value: 'certificate',
          },
          { text: ownProps.translator.__('Marketplace'), value: 'marketplace' },
          {
            text: ownProps.translator.__('API/OTE'),
            value: 'api',
          },
        ],
        [ownProps.translator.__('Other issues')]: [
          { text: ownProps.translator.__('Payment'), value: 'billing' },
          {
            text: ownProps.translator.__('Account/Organization'),
            value: 'account',
          },
          { text: ownProps.translator.__('Migration'), value: 'migration' },
        ],
        [ownProps.translator.__('Suggestions & Feedback')]: [
          {
            text: ownProps.translator.__('Suggestions & Feedback'),
            value: 'feedback',
          },
        ],
      },
    ],
    rangePortfolio: [
      { text: ownProps.translator.__('<100'), value: '<100' },
      { text: ownProps.translator.__('100 - 300'), value: '100 - 300' },
      { text: ownProps.translator.__('300 - 1000'), value: '300 - 1000' },
      { text: ownProps.translator.__('>1000'), value: '>1000' },
    ],
    tips: {
      'article-15_access': accessAndRectificationTips,
      'article-16_rectification': accessAndRectificationTips,
      'bill-method': [
        {
          type: 'info',
          message: ownProps.translator.__('bill-method_info', {
            withHTML: true,
          }),
        },
      ],
      'bill-prepaid': [
        {
          type: 'info',
          message: ownProps.translator.__('bill-prepaid_info', {
            withHTML: true,
          }),
        },
      ],
      'bill-check': [
        {
          type: 'info',
          message: ownProps.translator.__('bill-check_info', {
            withHTML: true,
          }),
        },
      ],
      'bill-wire': [
        {
          type: 'info',
          message: ownProps.translator.__('bill-wire_info', { withHTML: true }),
        },
      ],
      'bill-cancel': [
        {
          type: 'info',
          message: ownProps.translator.__('bill-cancel_info', {
            withHTML: true,
          }),
        },
      ],
      'bill-error': [
        {
          type: 'info',
          message: ownProps.translator.__(
            '<p>If you encountered an error while attempting to pay for your order, ' +
              'please provide us with:<br/>' +
              '- The error message you saw<br/>' +
              '- The steps you took right before you got the error<br/></p>' +
              '<p>The more information you provide to us in the form below, ' +
              'the better we will be able to help you.</p>' +
              '<p>We apologize for the inconvenience, and a level 2 agent ' +
              'will get back to you as soon as possible.</p>',
            {
              withHTML: true,
            }
          ),
        },
      ],
      'dn-renw': [
        {
          type: 'info',
          message: ownProps.translator.__('dn-renw_info', { withHTML: true }),
        },
      ],
      'dn-buy': [
        {
          type: 'info',
          message: ownProps.translator.__('dn-buy_info', { withHTML: true }),
        },
      ],
      'dn-trans': [
        {
          type: 'info',
          message: ownProps.translator.__('dn-trans_info', { withHTML: true }),
        },
      ],
      'dn-dns': [
        {
          type: 'info',
          message: ownProps.translator.__('dn-dns_info', { withHTML: true }),
        },
      ],
      'dn-zone': [
        {
          type: 'info',
          message: ownProps.translator.__('dn-zone_info', { withHTML: true }),
        },
      ],
      'dn-dnssec': [
        {
          type: 'info',
          message: ownProps.translator.__(
            'It is possible to use DNSSEC with your domain names at Gandi if ' +
              'you use our LiveDNS or if you are using 3rd party nameservers that ' +
              'support it.<br><br> However, <strong>if you do not see DNSSEC</strong>, ' +
              "in your domain's navigation menu, it means your domain is not using our " +
              'LiveDNS nameservers, or is in an extension that does not allow DNSSEC at Gandi. <br><br>' +
              'For more information on how to do this please see our ' +
              '<a href="https://docs.gandi.net/en/domain_names/advanced_users/dnssec.html">online DNSSEC documentation</a>.',
            { withHTML: true }
          ),
        },
      ],
      'dn-handle': [
        {
          type: 'info',
          message: ownProps.translator.__('dn-handle_info', { withHTML: true }),
        },
      ],
      'dn-whois': [
        {
          type: 'info',
          message: ownProps.translator.__('dn-whois_info', { withHTML: true }),
        },
      ],
      'dn-owner': [
        {
          type: 'info',
          message: ownProps.translator.__('dn-owner_info', { withHTML: true }),
        },
      ],
      'dn-redir': [
        {
          type: 'info',
          message: ownProps.translator.__('dn-redir_info', { withHTML: true }),
        },
      ],
      'dn-cost': [
        {
          type: 'info',
          message: ownProps.translator.__('dn-cost_info', { withHTML: true }),
        },
      ],
      'dn-status': [
        {
          type: 'info',
          message: ownProps.translator.__(
            '- <strong>Is your order stuck in the payment phase?</strong> Orders are not launched ' +
              'until after the payment has cleared both at Gandi and our payment gateway. ' +
              'This may take several days, depending on the payment means used. ' +
              '<a href="https://docs.gandi.net/en/billing/means_of_payment/index.html" target="_blank" rel="noopener">Learn more</a><br><br>' +
              '- <strong>Is your domain registration stuck?</strong> ' +
              '<a href="https://docs.gandi.net/en/domain_names/register/followup.html" target="_blank" rel="noopener">See why here.</a><br><br>' +
              '- <strong>Is your domain transfer stuck?</strong> ' +
              '<a href="https://docs.gandi.net/en/domain_names/transfer/followup.html" target="_blank" rel="noopener">See why here.</a><br><br>' +
              "If you can't find the reason for your order being stuck in ocd ne of the above links, " +
              'or if you can not find how to unblock the situation, please let us know below.',
            { withHTML: true }
          ),
        },
      ],
      'dn-other': [
        {
          type: 'info',
          message: ownProps.translator.__('dn-other_info', { withHTML: true }),
        },
      ],
      'host-renw': [
        {
          type: 'info',
          message: ownProps.translator.__(
            'To keep your resources running, it is necessary that your Gandi ' +
              'Cloud Account has a positive balance.<br><br>' +
              'If your balance is in the negative, and your servers are halted, you will need to add enough Gandi ' +
              'Cloud Account Credit to compensate for any negative balance due to services still being billed, but halted, ' +
              'in addition to an amount corresponding to how long you would like your Cloud resources to run.<br><br>' +
              'For more on this, please consult our <a href="https://doc.gandi.net/en/cloud/billing#how_to_add_credit">online documentation</a>.',
            { withHTML: true }
          ),
        },
      ],
      // 'host-mdp': [
      //   {
      //     type: 'info',
      //     message: ownProps.translator.__('', { withHTML: true }),
      //   },
      // ],
      'host-trans': [
        {
          type: 'info',
          message: ownProps.translator.__(
            'We are currently working on adding this feature. In the meantime, ' +
              'the only solution consists of creating a new server under the desired Gandi username or organization, ' +
              "and then reinstalling the services, followed by transferring the files via 'rsync' or 'scp' to the new server.",
            { withHTML: true }
          ),
        },
      ],
      'host-bloc': [
        {
          type: 'info',
          message: ownProps.translator.__(
            'If you are able to log into your server via SSH or via the Emergency Console, ' +
              'please verify:<br><br>' +
              "<ul><li>The system logs and those of the critical services ('/var/log' in general)</li>" +
              '<li>The resources used by the server:</li>' +
              '<ul><li>`<code>free -m</code>` to see your RAM use</li>' +
              '<li>`<code>df -h</code>` to see the disk</li>' +
              '<li>`<code>ps faux</code>` to see the list of running processes</li></ul></ul>' +
              'If, despite this, you are unable to determine the cause of the error,  please check the box on this form indicating ' +
              'that your server is  blocked, so that our experts can process your case in priority.',
            { withHTML: true }
          ),
        },
      ],
      'host-monan': [
        {
          type: 'info',
          message: ownProps.translator.__(
            'Cloud hosting resources are paid for by the Cloud Account credit associated ' +
              'to the user or organization that owns the resources.<br><br>' +
              'You can add credit by adding credits, and choosing one of the following at checkout:<br>' +
              '<ul><li>your prepaid account (which must first be credited via one of the methods listed below)</li>' +
              '<li>credit/debit card</li>' +
              '<li>Paypal</li>' +
              '<li>Bitcoin</li>' +
              '<li>Bank transfer ⚠ Delayed Payment (please account for 1 to 2 weeks for your payment validation)</li>' +
              '<li>French Check in Euros ⚠ Delayed Payment (please account for 7 days to validate your payment)</li>' +
              '<li>French Mandat administratif (only for French public bodies)</li></ul>' +
              'Alternatively, you may save a payment method and let our system keep your resources running by automatically ' +
              'having your card be debited 2 days prior to your credits running out.<br>' +
              'More information on this can be found in <a href="https://doc.gandi.net/en/cloud/billing">our public documentation</a>.',
            { withHTML: true }
          ),
        },
      ],
      'host-cost': [
        {
          type: 'info',
          message: ownProps.translator.__(
            "Gandi's prices vary, depending on which price rate you have reached. " +
              "You can see your current rate from your account's Cloud overview page.<br><br>" +
              'The total amount of your orders over the last 12 rolling months,  excluding tax, is automatically ' +
              'calculated and your account is moved to  the appropriate price rate.<br><br>' +
              'You can view our hosting prices on <a href="https://doc.gandi.net/en/cloud">our Cloud description page</a>.',
            { withHTML: true }
          ),
        },
      ],
      'host-cancel': [
        {
          type: 'info',
          message: ownProps.translator.__(
            "Please note that Cloud credit can't be refunded as stipulated in the " +
              '<a href="https://www.gandi.net/en/contracts/terms-of-service">Terms and Conditions contract</a>. Consequently, if you no longer want to use a server, we recommend that you ' +
              'delete it as well as all the resources attached to it so that it stops consuming credit.<br> Your Cloud credit is ' +
              'valid for two years starting from the date it was last used. If the credit is not consumed within that two-year ' +
              'period, they will be automatically deleted as per our hosting contract.',
            { withHTML: true }
          ),
        },
      ],
      'host-other': [
        {
          type: 'info',
          message: ownProps.translator.__(
            'We are still in the process of rolling out the new Cloud hosting on our Gandi ' +
              "v5 website, so there may be some features you are looking for and can't find. If this is the case, please use " +
              'the form below to let us know what you are looking for or how we can help.',
            { withHTML: true }
          ),
        },
      ],
      'mail-pack': [
        {
          type: 'info',
          message: ownProps.translator.__('mail-pack_info', { withHTML: true }),
        },
      ],
      'mail-mx': [
        {
          type: 'info',
          message: ownProps.translator.__('mail-mx_info', { withHTML: true }),
        },
      ],
      'mail-mdp': [
        {
          type: 'info',
          message: ownProps.translator.__('mail-mdp_info', { withHTML: true }),
        },
      ],
      'mail-cost': [
        {
          type: 'info',
          message: ownProps.translator.__('mail-cost_info', { withHTML: true }),
        },
      ],
      'mail-other': [
        {
          type: 'info',
          message: ownProps.translator.__('mail-other_info', {
            withHTML: true,
          }),
        },
      ],
      'mail-add': [
        {
          type: 'info',
          message: ownProps.translator.__(
            'If you need to create more email mailboxes than the ones that come included with your ' +
              'domain name, you need to use the button on the Email page of your domain admin to ' +
              'buy more mailboxes.\n\nYou then choose the number and type (standard/premium) that you ' +
              'want, and then proceed to payment. This will provide you with more available ' +
              'mailboxes that you can use to create new email addresses.',
            { withMarkdown: true }
          ),
        },
      ],
      'mail-newoffer': [
        {
          type: 'info',
          message: ownProps.translator.__(
            'When you migrate to the new offer, we convert the resources that you paid for on ' +
              'the old platform to an equivalent offer on the new one. If you encountered a ' +
              'problem during this migration, or need help understanding the conversion, please ' +
              'let us know below.'
          ),
        },
      ],
      'mail-cancel': [
        {
          type: 'info',
          message: ownProps.translator.__(
            'To no longer pay for the renewal of your purchased gandimail mailboxes during domain ' +
              'renewal, you must "release" them by using the "Release Mailbox" option from the ' +
              "email page of your domain's admin.\n\nWe then refund to your prepaid account, the " +
              'remaining amount that you paid for but did not use. You will also not be charged ' +
              'for their renewal during your domain renewal',
            { withMarkdown: true }
          ),
        },
      ],
      'mail-phishing': [
        {
          type: 'warning',
          message: ownProps.translator.__(
            '<p>If you received an email that <strong>appears to be from your own email address</strong> ' +
              'and claims that your email address has been hacked, there is no need to worry. ' +
              'This is a common phishing scam, where the sender <strong>fakes</strong> the email ' +
              'address to look like it is from you. It is not (see more ' +
              'on <a href="https://docs.gandi.net/en/gandimail/faq/spoofing.html">spoofing</a>).</p>' +
              '<p>Do not reply to such mails, or take any action that the scammer wants you to perform. ' +
              'Their only goal is to get you to panic.</p>' +
              '<p><strong>You can safely disregard such emails and delete them directly</strong>. There is ' +
              '<strong>no need to forward them to our support team</strong> or alert us, as it is a known and ' +
              'pretty common annoyance.</p>',
            { withHTML: true }
          ),
        },
      ],
      'account-email': [
        {
          type: 'info',
          message: ownProps.translator.__('other-email_info', {
            withHTML: true,
          }),
        },
      ],
      'account-password': [
        {
          type: 'info',
          message: ownProps.translator.__('other-mdp_info', { withHTML: true }),
        },
      ],
      'account-totp': [
        {
          type: 'info',
          message: ownProps.translator.__('other-totp_info', {
            withHTML: true,
          }),
        },
      ],
      'account-other': [
        {
          type: 'info',
          message: ownProps.translator.__('other-bug_info', { withHTML: true }),
        },
      ],
      'api-start': [
        {
          type: 'info',
          message: ownProps.translator.__(
            '<p>We are currently in the process of rolling out the public ' +
              'RESTful APIs for our new website, #gandiv5.</p><p>You can refer to our API documentation ' +
              'at <a href="https://api.gandi.net/docs/">https://api.gandi.net/docs/</a> which will be ' +
              'constantly updated as we proceed.</p><p>We do not yet have an OTE environment.</p>' +
              '<p>For information on getting started, the workflows, obtaining your public API key, and other ' +
              'FAQs please see our <a href="https://docs.gandi.net/en/reseller/faq/index.html">general ' +
              'documentation</a>.</p>',
            { withHTML: true }
          ),
        },
      ],
      'api-errors': [
        {
          type: 'info',
          message: ownProps.translator.__(
            '<p>If you encounter problems when using the API and need our ' +
              'help, please let us know:</p>' +
              '<ul><li>The date and time of the error,</li>' +
              '<li>The route, parameters, and query string you are using, if applicable,</li>' +
              '<li>the response that you obtained,</li>' +
              '<li>A simple example of the use of the API that returns the error (sample code) ' +
              'or what you are trying to do.</li></ul>' +
              '<p>This information will allow us to rapidly analyze the problem and provide you ' +
              'a solution. Please also let us know what Gandi account (username) you are using ' +
              'to connect with.</p>' +
              '<p>For security reasons, please never send us your production API code.</p>',
            { withHTML: true }
          ),
        },
      ],
      'shs-transfer-file': [
        {
          type: 'info',
          message: ownProps.translator.__('shs-transfer-file_info', {
            withHTML: true,
          }),
        },
      ],
      'shs-db': [
        {
          type: 'info',
          message: ownProps.translator.__('shs-db_info', { withHTML: true }),
        },
      ],
      'shs-renew': [
        {
          type: 'info',
          message: ownProps.translator.__('shs-renew_info', { withHTML: true }),
        },
      ],
      'shs-attach-domain': [
        {
          type: 'info',
          message: ownProps.translator.__('shs-attach-domain_info', {
            withHTML: true,
          }),
        },
      ],
      'shs-quota': [
        {
          type: 'info',
          message: ownProps.translator.__('shs-quota_info', { withHTML: true }),
        },
      ],
      'shs-price': [
        {
          type: 'info',
          message: ownProps.translator.__('shs-price_info', { withHTML: true }),
        },
      ],
      'shs-mode-payment': [
        {
          type: 'info',
          message: ownProps.translator.__('shs-mode-payment_info', {
            withHTML: true,
          }),
        },
      ],
      'shs-cancel': [
        {
          type: 'info',
          message: ownProps.translator.__('shs-cancel_info', {
            withHTML: true,
          }),
        },
      ],
      'shs-change-owner': [
        {
          type: 'info',
          message: ownProps.translator.__('shs-change-owner_info', {
            withHTML: true,
          }),
        },
      ],
      'shs-other': [
        {
          type: 'info',
          message: ownProps.translator.__('shs-other_info', { withHTML: true }),
        },
      ],
      'vps-bloc': [
        {
          type: 'info',
          message: ownProps.translator.__(
            'If you are able to log into your server via SSH or via the Emergency Console, ' +
              'please verify:<br><br>' +
              "<ul><li>The system logs and those of the critical services ('/var/log' in general)</li>" +
              '<li>The resources used by the server:</li>' +
              '<ul><li>`<code>free -m</code>` to see your RAM use</li>' +
              '<li>`<code>df -h</code>` to see the disk</li>' +
              '<li>`<code>ps faux</code>` to see the list of running processes</li></ul></ul>' +
              'If, despite this, you are unable to determine the cause of the error,  please check the box on this form indicating ' +
              'that your server is  blocked, so that our experts can process your case in priority.',
            { withHTML: true }
          ),
        },
      ],
    },
    topics: {
      billing: [
        {
          text: ownProps.translator.__('Available means of payment'),
          value: 'bill-method',
        },
        {
          text: ownProps.translator.__('Prepaid account'),
          value: 'bill-prepaid',
        },
        { text: ownProps.translator.__('Check'), value: 'bill-check' },
        { text: ownProps.translator.__('PayPal'), value: 'bill-paypal' },
        { text: ownProps.translator.__('Bank transfer'), value: 'bill-wire' },
        {
          text: ownProps.translator.__('Your payment has been rejected'),
          value: 'bill-cancel',
        },
        {
          text: ownProps.translator.__('Website error at payment'),
          value: 'bill-error',
        },
        {
          text: ownProps.translator.__('Suggestions & Feedback'),
          value: 'bill-feedback',
        },
        { text: ownProps.translator.__('Other'), value: 'bill-other' },
      ],
      certificate: [
        { text: ownProps.translator.__('Purchase'), value: 'ssl-buy' },
        { text: ownProps.translator.__('Renewal'), value: 'ssl-renew' },
        { text: ownProps.translator.__('Revocation'), value: 'ssl-del' },
        { text: ownProps.translator.__('Activation'), value: 'ssl-valid' },
        {
          text: ownProps.translator.__('Suggestions & Feedback'),
          value: 'ssl-feedback',
        },
        { text: ownProps.translator.__('Other'), value: 'ssl-other' },
      ],
      'corporate-contact': [
        {
          text: ownProps.translator.__('Advice'),
          value: ownProps.translator.__('Advice'),
        },
        {
          text: ownProps.translator.__('Portfolio Migration'),
          value: ownProps.translator.__('Portfolio Migration'),
        },
        {
          text: ownProps.translator.__('New Registrations'),
          value: ownProps.translator.__('New Registrations'),
        },
        {
          text: ownProps.translator.__('Other'),
          value: ownProps.translator.__('Other'),
        },
      ],
      domain: [
        { text: ownProps.translator.__('Renewal'), value: 'dn-renw' },
        { text: ownProps.translator.__('Purchase'), value: 'dn-buy' },
        { text: ownProps.translator.__('Transfer'), value: 'dn-trans' },
        { text: ownProps.translator.__('DNS change'), value: 'dn-dns' },
        { text: ownProps.translator.__('DNS zone'), value: 'dn-zone' },
        { text: ownProps.translator.__('DNSSEC'), value: 'dn-dnssec' },
        { text: ownProps.translator.__('Contact problem'), value: 'dn-handle' },
        { text: ownProps.translator.__('Whois'), value: 'dn-whois' },
        { text: ownProps.translator.__('Owner'), value: 'dn-owner' },
        {
          text: ownProps.translator.__('Forwarding/mapping'),
          value: 'dn-redir',
        },
        { text: ownProps.translator.__('Cancellation'), value: 'dn-cancel' },
        { text: ownProps.translator.__('Prices / rates'), value: 'dn-cost' },
        { text: ownProps.translator.__('Order status'), value: 'dn-status' },
        {
          text: ownProps.translator.__('Suggestions & Feedback'),
          value: 'dn-feedback',
        },
        { text: ownProps.translator.__('Other'), value: 'dn-other' },
      ],
      vps: [
        {
          text: ownProps.translator.__('Server creation'),
          value: 'vps-creation',
        },
        {
          text: ownProps.translator.__('Credentials / Access issue '),
          value: 'vps-access',
        },
        { text: ownProps.translator.__('Renewal'), value: 'vps-renw' },
        { text: ownProps.translator.__('Blocked server'), value: 'vps-bloc' },
        {
          text: ownProps.translator.__('Billing /Payment method'),
          value: 'vps-bill',
        },
        { text: ownProps.translator.__('Cancellation'), value: 'vps-cancel' },
        {
          text: ownProps.translator.__('Suggestions & Feedback'),
          value: 'vps-feedback',
        },
        { text: ownProps.translator.__('Other'), value: 'vps-other' },
      ],
      hosting: [
        { text: ownProps.translator.__('Renewal'), value: 'host-renw' },
        { text: ownProps.translator.__('Password'), value: 'host-mdp' },
        { text: ownProps.translator.__('Owner change'), value: 'host-trans' },
        { text: ownProps.translator.__('Blocked server'), value: 'host-bloc' },
        {
          text: ownProps.translator.__('Means of payment'),
          value: 'host-monan',
        },
        { text: ownProps.translator.__('Prices / rates'), value: 'host-cost' },
        { text: ownProps.translator.__('Cancellation'), value: 'host-cancel' },
        {
          text: ownProps.translator.__('Suggestions & Feedback'),
          value: 'host-feedback',
        },
        { text: ownProps.translator.__('Other'), value: 'host-other' },
      ],
      gandimail: [
        { text: ownProps.translator.__('Mail Pack'), value: 'mail-pack' },
        { text: ownProps.translator.__('Configuration'), value: 'mail-mx' },
        { text: ownProps.translator.__('Password'), value: 'mail-mdp' },
        { text: ownProps.translator.__('Rates'), value: 'mail-cost' },
        {
          text: ownProps.translator.__('Additional mailboxes'),
          value: 'mail-add',
        },
        {
          text: ownProps.translator.__('Migration to new email offer'),
          value: 'mail-newoffer',
        },
        { text: ownProps.translator.__('Cancellation'), value: 'mail-cancel' },
        {
          text: ownProps.translator.__('Suspicious "hacked"/phishing email'),
          value: 'mail-phishing',
        },
        {
          text: ownProps.translator.__('Problems sending email'),
          value: 'mail-sending-problems',
        },
        {
          text: ownProps.translator.__('Problems receiving email'),
          value: 'mail-receiving-problems',
        },
        {
          text: ownProps.translator.__('Suggestions & Feedback'),
          value: 'mail-feedback',
        },
        { text: ownProps.translator.__('Other'), value: 'mail-other' },
      ],
      marketplace: [
        {
          text: ownProps.translator.__('Go Sitebuilder'),
          value: 'marketplace-sitebuilder',
        },
        {
          text: ownProps.translator.__('Suggestions & Feedback'),
          value: 'marketplace-feedback',
        },
        { text: ownProps.translator.__('Other'), value: 'marketplace-other' },
      ],
      migration: [
        {
          text: ownProps.translator.__("I can't complete account migration"),
          value: 'migra-account',
        },
        {
          text: ownProps.translator.__("I can't see my domain names"),
          value: 'migra-domain',
        },
        { text: ownProps.translator.__('Other'), value: 'migra-other' },
      ],
      'my-rights': [
        {
          text: ownProps.translator.__('Right of access (Article 15)'),
          value: 'article-15_access',
        },
        {
          text: ownProps.translator.__('Right to rectification (Article 16)'),
          value: 'article-16_rectification',
        },
        {
          text: ownProps.translator.__('Right to erasure (Article 17)'),
          value: 'article-17_erasure',
        },
        {
          text: ownProps.translator.__(
            'Right to restriction of processing (Article 18)'
          ),
          value: 'article-18_restriction_of_processing',
        },
        {
          text: ownProps.translator.__(
            'Right to data portability (Article 20)'
          ),
          value: 'article-20_data_portability',
        },
        {
          text: ownProps.translator.__('Right to object (Article 21)'),
          value: 'article-21_objection',
        },
      ],
      account: [
        {
          text: ownProps.translator.__('Merging accounts or organizations'),
          value: 'account-merge',
        },
        {
          text: ownProps.translator.__('Changing account name'),
          value: 'account-name',
        },
        {
          text: ownProps.translator.__('Problems logging in'),
          value: 'account-login',
        },
        {
          text: ownProps.translator.__('Modification of an email address'),
          value: 'account-email',
        },
        {
          text: ownProps.translator.__('Modification of a password'),
          value: 'account-password',
        },
        {
          text: ownProps.translator.__('2-factor authentification'),
          value: 'account-totp',
        },
        {
          text: ownProps.translator.__('Delete account'),
          value: 'account-delete',
        },
        {
          text: ownProps.translator.__('Suggestions & Feedback'),
          value: 'account-feedback',
        },
        { text: ownProps.translator.__('Other'), value: 'account-other' },
      ],
      api: [
        {
          text: ownProps.translator.__('Getting Started'),
          value: 'api-start',
        },
        {
          text: ownProps.translator.__('Errors'),
          value: 'api-errors',
        },
        {
          text: ownProps.translator.__('Suggestions & Feedback'),
          value: 'api-feedback',
        },
      ],
      shs: [
        {
          text: ownProps.translator.__('File transfer'),
          value: 'shs-transfer-file',
        },
        { text: ownProps.translator.__('Database'), value: 'shs-db' },
        { text: ownProps.translator.__('Renewal'), value: 'shs-renew' },
        {
          text: ownProps.translator.__('Attach to a domain'),
          value: 'shs-attach-domain',
        },
        {
          text: ownProps.translator.__('Disk quota / bandwidth'),
          value: 'shs-quota',
        },
        { text: ownProps.translator.__('Prices'), value: 'shs-price' },
        {
          text: ownProps.translator.__('Payment means'),
          value: 'shs-mode-payment',
        },
        { text: ownProps.translator.__('Cancellation'), value: 'shs-cancel' },
        {
          text: ownProps.translator.__('Change the owner'),
          value: 'shs-change-owner',
        },
        {
          text: ownProps.translator.__('Suggestions & Feedback'),
          value: 'shs-feedback',
        },
        { text: ownProps.translator.__('Other'), value: 'shs-other' },
      ],
    },
  };
};

export default (compose(
  // $FlowFixMe[underconstrained-implicit-instantiation]
  withRouter,
  withTranslator({ propsNamespace: 'translator' }),
  // $FlowFixMe[incompatible-call]
  connect(mapStateToProps, mapActionsToProps, mergeProps)
)(ContactSupportForm): React.ComponentType<OwnProps>);
